/**
 * Utilities for column reorganization in PriceRoute component
 * Handles transformation between 'scenario-first' and 'elo-first' column organizations
 */

/**
 * Generates visible columns based on organization type and metadata
 * @param {Object} visibleColumns - Object containing visibility state for each column
 * @param {Object} metadata - Backend metadata containing node and column definitions
 * @param {string} columnOrganization - 'scenario-first' | 'elo-first'
 * @returns {Array} Array of column objects with proper structure
 */
export const generateVisibleColumns = (
  visibleColumns,
  metadata,
  columnOrganization = "scenario-first",
) => {
  console.log("Generating visible columns:", {
    visibleColumns,
    metadata,
    columnOrganization,
  });

  if (!metadata || !metadata.nodes) {
    console.warn("No metadata provided to generateVisibleColumns");
    return [];
  }

  const scenarios = ["asis", "tobe"]; // Internal scenario names
  const columns = [];

  // Get all possible columns from metadata
  Object.keys(metadata.nodes).forEach((nodeKey) => {
    const nodeMetrics = metadata.nodes[nodeKey].columns || [];

    nodeMetrics.forEach((metric) => {
      // Handle delta_rate columns differently - they only have delta values, not asis/tobe
      if (metric.calculation_type === "delta_rate") {
        const columnKey = `delta_${nodeKey}_${metric.column_id}`;

        // Only include columns that are visible
        if (visibleColumns[columnKey]) {
          columns.push({
            key: columnKey,
            nodeKey,
            scenario: "delta",
            scenarioIndex: 2, // Delta comes after asis (0) and tobe (1)
            metric: metric.column_id,
            name: metric.column_name,
            fullKey: `delta.${nodeKey}.${metric.column_id}`,
            formula: metric.delta_formula,
            calculationType: metric.calculation_type,
          });
        }
      } else {
        // Regular columns have both asis and tobe values
        scenarios.forEach((scenario, scenarioIndex) => {
          const columnKey = `${scenario}_${nodeKey}_${metric.column_id}`;

          // Only include columns that are visible
          if (visibleColumns[columnKey]) {
            columns.push({
              key: columnKey,
              nodeKey,
              scenario,
              scenarioIndex,
              metric: metric.column_id,
              name: metric.column_name,
              fullKey: `${scenario}.${nodeKey}.${metric.column_id}`,
              formula:
                scenario === "asis" ? metric.asis_formula : metric.tobe_formula,
              calculationType: metric.calculation_type,
            });
          }
        });
      }
    });
  });

  // Sort columns based on organization
  if (columnOrganization === "scenario-first") {
    // Sort by scenario first, then node, then metric
    columns.sort((a, b) => {
      if (a.scenarioIndex !== b.scenarioIndex)
        return a.scenarioIndex - b.scenarioIndex;
      if (a.nodeKey !== b.nodeKey) return a.nodeKey.localeCompare(b.nodeKey);
      return a.metric.localeCompare(b.metric);
    });
  } else {
    // Sort by node first, then metric, then scenario
    columns.sort((a, b) => {
      if (a.nodeKey !== b.nodeKey) return a.nodeKey.localeCompare(b.nodeKey);
      if (a.metric !== b.metric) return a.metric.localeCompare(b.metric);
      return a.scenarioIndex - b.scenarioIndex;
    });
  }

  console.log("Generated columns:", columns);
  return columns;
};

/**
 * Maps data from hierarchical structure to column values
 * @param {Object} rowData - Row data containing asis/tobe/elo structure and flattened values
 * @param {Object} visibleColumns - Visible columns object (used as columns parameter)
 * @param {string} columnOrganization - Column organization type
 * @returns {Object} Object with column keys mapped to values
 */
export const mapDataToColumns = (
  rowData,
  visibleColumns,
  _columnOrganization = "scenario-first", // eslint-disable-line no-unused-vars -- Reserved for future use in data mapping logic
) => {
  const values = {};

  console.log("mapDataToColumns called with:", {
    hasRowData: !!rowData,
    hasValues: rowData && rowData.values,
    valuesType: typeof rowData?.values,
    visibleColumnsType: typeof visibleColumns,
    visibleColumnsKeys: visibleColumns
      ? Object.keys(visibleColumns).slice(0, 5)
      : null,
    rowDataKeys: Object.keys(rowData || {}),
    valuesKeys: rowData?.values
      ? Object.keys(rowData.values).slice(0, 10)
      : null,
  });

  // If the backend provides a pre-flattened values object, use it directly
  if (rowData && rowData.values && typeof rowData.values === "object") {
    console.log("Using pre-flattened values object from backend");

    // If visibleColumns is an object (visibility state), get all keys
    if (visibleColumns && typeof visibleColumns === "object") {
      Object.keys(visibleColumns).forEach((columnKey) => {
        if (visibleColumns[columnKey]) {
          // Only include visible columns
          values[columnKey] =
            rowData.values[columnKey] !== undefined
              ? rowData.values[columnKey]
              : null;
        }
      });
    }

    console.log(
      "Mapped values from backend:",
      Object.keys(values).length,
      "columns",
    );
    return values;
  }

  // Fallback to navigating the hierarchical structure if no flattened values are available
  if (visibleColumns && typeof visibleColumns === "object") {
    Object.keys(visibleColumns).forEach((columnKey) => {
      if (visibleColumns[columnKey]) {
        // Only include visible columns
        // Parse column key: format is "scenario_nodeKey_metric"
        const parts = columnKey.split("_");
        if (parts.length >= 3) {
          const scenario = parts[0]; // asis or tobe
          const nodeKey = parts[1]; // elo1, elo2, elo3
          const metric = parts.slice(2).join("_"); // metric name (may contain underscores)

          try {
            // Navigate the hierarchical data structure: rowData[scenario][nodeKey][metric]
            const scenarioData = rowData[scenario];
            const nodeData = scenarioData ? scenarioData[nodeKey] : null;
            const metricValue = nodeData ? nodeData[metric] : null;

            values[columnKey] = metricValue !== undefined ? metricValue : null;
          } catch (error) {
            console.warn("Error mapping data for column:", columnKey, error);
            values[columnKey] = null;
          }
        } else {
          console.warn("Invalid column key format:", columnKey);
          values[columnKey] = null;
        }
      }
    });
  }

  return values;
};

/**
 * Generates header structure for table rendering
 * @param {string} columnOrganization - 'scenario-first' | 'elo-first'
 * @param {Object} visibleNodes - Visible nodes object
 * @param {Array} selectedScenarios - Selected scenarios array
 * @param {Object} nodeStructure - Node structure with names
 * @param {Object} metadata - Backend metadata containing node definitions
 * @returns {Object} Header structure for table rendering
 */
export const generateHeaderStructure = (
  columnOrganization,
  visibleNodes,
  selectedScenarios,
  nodeStructure,
  metadata,
) => {
  const visibleNodeKeys = Object.keys(visibleNodes).filter(
    (nodeKey) => visibleNodes[nodeKey],
  );
  const scenarios = [
    {
      key: "asis",
      name:
        selectedScenarios[0]?.scenario_name || selectedScenarios[0] || "AS IS",
    },
    {
      key: "tobe",
      name:
        selectedScenarios[1]?.scenario_name || selectedScenarios[1] || "TO BE",
    },
  ];

  if (columnOrganization === "scenario-first") {
    // Scenario-first: AS IS (all elos), then TO BE (all elos)
    return {
      type: "scenario-first",
      structure: scenarios.map((scenario) => ({
        ...scenario,
        colSpan: visibleNodeKeys.reduce(
          (sum, nodeKey) => sum + getMetricsCount(nodeKey, metadata),
          0,
        ),
        nodes: visibleNodeKeys.map((nodeKey) => ({
          key: nodeKey,
          name: nodeStructure[nodeKey]?.name || nodeKey,
          colSpan: getMetricsCount(nodeKey, metadata),
        })),
      })),
    };
  } else {
    // Elo-first: Each node with metrics, each metric has AS IS and TO BE
    return {
      type: "elo-first",
      structure: visibleNodeKeys.map((nodeKey) => ({
        key: nodeKey,
        name: nodeStructure[nodeKey]?.name || nodeKey,
        colSpan: getMetricsCount(nodeKey, metadata) * 2, // Each metric has 2 scenarios
        metrics: getMetricsForNode(nodeKey, metadata).map((metric) => ({
          key: metric.key,
          name: metric.name,
          colSpan: 2, // AS IS and TO BE for each metric
          scenarios: scenarios,
        })),
      })),
    };
  }
};

/**
 * Helper function to get metrics count for a node using metadata
 * @param {string} nodeKey - Node key (elo1, elo2, elo3)
 * @param {Object} metadata - Backend metadata containing node definitions
 * @returns {number} Number of metrics for the node
 */
const getMetricsCount = (nodeKey, metadata) => {
  if (!metadata || !metadata.nodes || !metadata.nodes[nodeKey]) {
    return 0;
  }
  return metadata.nodes[nodeKey].columns?.length || 0;
};

/**
 * Helper function to get metrics array for a node using metadata
 * @param {string} nodeKey - Node key (elo1, elo2, elo3)
 * @param {Object} metadata - Backend metadata containing node definitions
 * @returns {Array} Array of metrics for the node
 */
const getMetricsForNode = (nodeKey, metadata) => {
  if (!metadata || !metadata.nodes || !metadata.nodes[nodeKey]) {
    return [];
  }

  const nodeColumns = metadata.nodes[nodeKey].columns || [];
  return nodeColumns.map((column) => ({
    key: column.column_id,
    name: column.column_name,
  }));
};

/**
 * Reorganizes existing columns array based on new organization
 * @param {Array} columns - Existing columns array
 * @param {string} newOrganization - New organization type
 * @returns {Array} Reorganized columns array
 */
export const reorganizeColumns = (columns, newOrganization) => {
  if (!columns || columns.length === 0) return columns;

  // Group columns by scenario and node for reorganization
  const grouped = columns.reduce((acc, col) => {
    const key = `${col.scenario}_${col.nodeKey}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(col);
    return acc;
  }, {});

  const reorganized = [];
  const scenarios = ["asis", "tobe"];
  const nodes = ["elo1", "elo2", "elo3"];

  if (newOrganization === "scenario-first") {
    // Scenarios → Nodes → Metrics
    scenarios.forEach((scenario) => {
      nodes.forEach((nodeKey) => {
        const key = `${scenario}_${nodeKey}`;
        if (grouped[key]) {
          reorganized.push(...grouped[key]);
        }
      });
    });
  } else {
    // Nodes → Scenarios → Metrics
    nodes.forEach((nodeKey) => {
      scenarios.forEach((scenario) => {
        const key = `${scenario}_${nodeKey}`;
        if (grouped[key]) {
          reorganized.push(...grouped[key]);
        }
      });
    });
  }

  return reorganized;
};

export default {
  generateVisibleColumns,
  mapDataToColumns,
  generateHeaderStructure,
  reorganizeColumns,
};
